import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'

import { DSButton, DSCol, DSRow, DSSelect } from '@vr/ds-react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'

import { IFormFilterProps } from './interface'
import { FormFilterRef } from './ref'
import { FormValues } from './type'
import CompanyId from '@/compositions/field-company-id'
import Contract from '@/compositions/field-contract'
import ItemDate from '@/compositions/field-item-date'
import { STATUS } from '@/constants/status'
import { formatBrDateToIso } from '@/utils/format-br-date-to-iso'
import { getLast31DaysPeriod } from '@/utils/get-last-31-days-period'

const STATUS_OPTIONS = Object.entries(STATUS).map(([key, val]) => ({
  label: val.label,
  value: key
}))

const dates = getLast31DaysPeriod().split(' - ')
const defaultStartDate = formatBrDateToIso(dates[0])
const defaultEndDate = formatBrDateToIso(dates[1])
const period = `${defaultStartDate}T23:59:59.999Z - ${defaultEndDate}T23:59:59.999Z`

const DEFAULT_VALUES: FormValues = {
  dataDoItem: period,
  companyId: '',
  contract: '',
  status: null
}

const FormFilter = forwardRef<FormFilterRef, IFormFilterProps>(
  ({ onFilters, onChange, onClear }, ref) => {
    const formRef = useRef<HTMLFormElement>(null)
    const methods = useForm<FormValues>({
      defaultValues: DEFAULT_VALUES,
      mode: 'onChange'
    })
    const watchedValues = useWatch({ control: methods.control })
    const [accountKey, setAccountKey] = useState(Date.now())

    const watchedDataDoItem = useWatch({
      name: 'dataDoItem',
      control: methods.control,
      defaultValue: period
    })

    useEffect(() => {
      setAccountKey(Date.now())
      methods.setValue('dataDoItem', watchedDataDoItem)
    }, [watchedDataDoItem])

    useEffect(() => {
      if (onChange) {
        onChange(watchedValues as FormValues)
      }
    }, [watchedValues, onChange])

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        formRef.current?.dispatchEvent(
          new Event('submit', { cancelable: true, bubbles: true })
        )
      },
      clearForm: () => {
        methods.reset(DEFAULT_VALUES)
      }
    }))

    const onSubmit = (data: FormValues) => {
      if (onFilters) onFilters(data)
    }

    return (
      <FormProvider {...methods}>
        <form
          id='buscar'
          onSubmit={methods.handleSubmit(onSubmit)}
          ref={formRef}
        >
          <DSRow wrap='wrap'>
            <DSCol xs={12} lg={4} gap={4}>
              <ItemDate methods={methods} />
            </DSCol>
            <DSCol xs={12} lg={4} gap={4}>
              <CompanyId control={methods.control} accountKey={accountKey} />
            </DSCol>
            <DSCol xs={12} lg={4} gap={4}>
              <Contract control={methods.control} accountKey={accountKey} />
            </DSCol>
          </DSRow>
          <DSRow wrap='wrap' css={{ marginTop: '$sm16' }}>
            <DSCol xs={12} lg={4} gap={4}>
              <Controller
                name='status'
                control={methods.control}
                render={({ field }) => (
                  <DSSelect
                    label='Status'
                    placeholder='Selecione o status'
                    selected={
                      STATUS_OPTIONS.find(o => o.value === field.value) ?? null
                    }
                    onSelect={option => {
                      if (option && !Array.isArray(option)) {
                        field.onChange(option.value)
                      } else {
                        field.onChange(null)
                      }
                    }}
                    onClear={() => field.onChange(null)}
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <DSSelect.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </DSSelect.Option>
                    ))}
                  </DSSelect>
                )}
              />
            </DSCol>
            <DSCol
              xs={12}
              lg={8}
              gap={4}
              direction='row'
              style={{ display: 'flex', alignItems: 'flex-end' }}
              justify='end'
            >
              <DSButton
                variant='ghost'
                iconLeft='principal-trash'
                outlined
                onClick={onClear}
              >
                Limpar
              </DSButton>
              <DSButton
                variant='primary'
                iconLeft='principal-filter'
                type='submit'
                disabled={!!methods.formState.errors.dataDoItem}
              >
                Filtrar
              </DSButton>
            </DSCol>
          </DSRow>
        </form>
      </FormProvider>
    )
  }
)
FormFilter.displayName = 'FormFilter'

export { FormFilter }
